import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import {
  FiSearch,
  FiSliders,
  FiCheckSquare,
  FiLogOut,
  FiCalendar,
} from "react-icons/fi";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import { useSelector } from "react-redux";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

function GroupList({ onClose, zIndex, openReport }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeFyid = useSelector(selectActiveFyid);
  const activeFy = useSelector(selectActiveFy);
  const fyid = activeFyid || Number(sessionStorage.getItem("selectedFyId")) || Number(localStorage.getItem("selectedFyId")) || 0;

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  // --- Form States ---
  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = getCurrentDate();

  const groupNameRef = useRef(null);
  const [groupName, setGroupName] = useState({ value: "", label: "" });
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);
  const [balanceFilter, setBalanceFilter] = useState("All");
  const [reportType, setReportType] = useState("Summary");
  const [activeTab, setActiveTab] = useState("Area");
  const [excludeSubGroup, setExcludeSubGroup] = useState(false);

  // --- Search Filters ---
  const [routeSearch, setRouteSearch] = useState("");
  const [areaSearch, setAreaSearch] = useState("");

  // --- Dynamic Option Lists ---
  const [routes, setRoutes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [deliveryDays, setDeliveryDays] = useState([
    { id: "Monday", name: "Monday", checked: false },
    { id: "Tuesday", name: "Tuesday", checked: false },
    { id: "Wednesday", name: "Wednesday", checked: false },
    { id: "Thursday", name: "Thursday", checked: false },
    { id: "Friday", name: "Friday", checked: false },
    { id: "Saturday", name: "Saturday", checked: false },
    { id: "Sunday", name: "Sunday", checked: false },
  ]);
  const [allowedAreaIds, setAllowedAreaIds] = useState(null);

  // --- Fetch Routes, Areas, Groups and Salesmen from API ---
  useEffect(() => {
    if (!dbCode) return;
    const fetchOptions = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/otherregistration/getoptions/${dbCode}`,
        );
        const data = response.data || [];

        // Map routes (Type === "Route" and Active !== 0)
        const fetchedRoutes = data
          .filter((item) => String(item.Type || item.type || "").toLowerCase() === "route" && item.Active !== 0)
          .map((item) => ({
            id: item.Auto ?? item.auto,
            name: item.Name || item.name,
            checked: false,
          }));

        // Map areas (Type === "City" and Active !== 0)
        const fetchedAreas = data
          .filter((item) => String(item.Type || item.type || "").toLowerCase() === "city" && item.Active !== 0)
          .map((item) => ({
            id: item.Auto ?? item.auto,
            name: item.Name || item.name,
            checked: false,
          }));

        setRoutes(fetchedRoutes);
        setAreas(fetchedAreas);
      } catch (error) {
        console.error("Error fetching routes and areas:", error);
      }
    };

    const fetchGroupOptions = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/spledgerheads/${dbCode}`,
        );
        const parDetails = response.data || [];
        const mapped = parDetails.map((item) => ({
          label: item.lh_name,
          value: item.lh_id,
        }));
        setGroupOptions(mapped);
      } catch (error) {
        console.error("Error fetching group options:", error);
      }
    };

    const fetchSalesmen = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/SchReg_empname/${dbCode}`,
        );
        const data = response.data || [];
        const fetchedSalesmen = data.map((item) => ({
          id: item.Auto,
          name: item.name,
          checked: false,
        }));
        setSalesmen(fetchedSalesmen);
      } catch (error) {
        console.error("Error fetching salesmen:", error);
      }
    };

    fetchOptions();
    fetchGroupOptions();
    fetchSalesmen();
  }, [apiBaseUrl, dbCode]);

  // --- Route-to-Area Filtering effect ---
  useEffect(() => {
    if (!dbCode) return;
    const checkedRouteIds = routes.filter((r) => r.checked).map((r) => r.id);
    if (checkedRouteIds.length === 0) {
      setAllowedAreaIds(null);
      return;
    }

    const fetchAllowedAreas = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/otherregistration/getareasbyroutes/${dbCode}?routes=${checkedRouteIds.join(",")}`,
        );
        setAllowedAreaIds(response.data || []);
      } catch (error) {
        console.error("Error fetching allowed areas by routes:", error);
      }
    };

    fetchAllowedAreas();
  }, [routes, dbCode, apiBaseUrl]);

  // --- Dynamic Font Loader ---
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // --- Close on Escape Key ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // --- List Handlers ---
  const handleRouteCheck = (id) => {
    setRoutes(
      routes.map((r) => (r.id === id ? { ...r, checked: !r.checked } : r)),
    );
  };

  const handleAreaCheck = (id) => {
    setAreas(
      areas.map((a) => (a.id === id ? { ...a, checked: !a.checked } : a)),
    );
  };

  const selectAllRoutes = () => {
    setRoutes(routes.map((r) => ({ ...r, checked: true })));
  };

  const unselectAllRoutes = () => {
    setRoutes(routes.map((r) => ({ ...r, checked: false })));
  };

  const selectAllAreas = () => {
    setAreas(areas.map((a) => ({ ...a, checked: true })));
  };

  const unselectAllAreas = () => {
    setAreas(areas.map((a) => ({ ...a, checked: false })));
  };

  const handleDeliveryDayCheck = (id) => {
    setDeliveryDays(
      deliveryDays.map((d) =>
        d.id === id ? { ...d, checked: !d.checked } : d,
      ),
    );
  };

  const selectAllDeliveryDays = () => {
    setDeliveryDays(deliveryDays.map((d) => ({ ...d, checked: true })));
  };

  const unselectAllDeliveryDays = () => {
    setDeliveryDays(deliveryDays.map((d) => ({ ...d, checked: false })));
  };

  const handleSalesmanCheck = (id) => {
    setSalesmen(
      salesmen.map((s) => (s.id === id ? { ...s, checked: !s.checked } : s)),
    );
  };

  const selectAllSalesmen = () => {
    setSalesmen(salesmen.map((s) => ({ ...s, checked: true })));
  };

  const unselectAllSalesmen = () => {
    setSalesmen(salesmen.map((s) => ({ ...s, checked: false })));
  };

  // Filter lists based on search text & constraints
  const filteredRoutes = routes.filter((r) =>
    r.name.toLowerCase().includes(routeSearch.toLowerCase()),
  );
  const filteredAreas = areas.filter((a) => {
    const matchesSearch = a.name
      .toLowerCase()
      .includes(areaSearch.toLowerCase());
    const matchesRoute =
      allowedAreaIds === null || allowedAreaIds.includes(a.id);
    return matchesSearch && matchesRoute;
  });

  return (
    <div
      className="absolute inset-0 w-full h-full bg-transparent flex items-center justify-center pointer-events-auto"
      style={{ zIndex: zIndex || 101 }}
    >
      <div
        className="group-list-container bg-white font-['Inter'] w-[620px] rounded-xl border border-slate-300 shadow-2xl flex flex-col absolute overflow-hidden transition-all duration-300"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <style>{`
          .group-list-container, .group-list-container * {
            font-family: 'Inter', sans-serif !important;
          }
        `}</style>
        {/* Header */}
        <WindowHeader
          title="Group List"
          logo={headeLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Content Body */}
        <div className="p-3 flex flex-col gap-1.5 overflow-hidden bg-white flex-1">
          {/* Group Name Row */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[11px] font-bold text-slate-800">
              Group Name
            </label>
            <SherComboBox
              id="groupName"
              name="groupName"
              value={groupName?.label || ""}
              comboRef={groupNameRef}
              options={groupOptions}
              onSelect={(opt) => setGroupName(opt)}
              onInputChange={(text) => {
                if (text === "") {
                  setGroupName({ value: "", label: "" });
                } else {
                  setGroupName({ value: text, label: text });
                }
              }}
              showArrow={true}
              clearable={false}
              placeholder="Select group name..."
              className="w-full h-[28px] outline-none"
              inputClassName="h-[28px] text-[11px] font-semibold text-slate-800 bg-white border border-slate-300 rounded outline-none focus:border-blue-600 w-full px-2 box-border"
            />
          </div>

          {/* Date Range Row */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold text-slate-800 shrink-0">
                From
              </label>
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val && !isDateInFy(val)) {
                    alert("Date incompatible with financial year");
                  }
                }}
                className="w-full h-[26px] px-2 border border-slate-300 rounded text-[11px] font-medium text-slate-700 outline-none bg-white cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold text-slate-800 shrink-0">
                To
              </label>
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val && !isDateInFy(val)) {
                    alert("Date incompatible with financial year");
                  }
                }}
                className="w-full h-[26px] px-2 border border-slate-300 rounded text-[11px] font-medium text-slate-700 outline-none bg-white cursor-pointer"
              />
            </div>
          </div>

          {/* Balance Filter Row */}
          <div className="flex items-center gap-4 py-0.5">
            {[
              { value: "All", label: "All" },
              { value: "Balance", label: "Balance" },
              { value: "Zero Balance", label: "Zero Balance" },
            ].map((item) => (
              <label
                key={item.value}
                className="flex items-center gap-1.5 cursor-pointer"
              >
                <input
                  type="radio"
                  name="balanceFilter"
                  checked={balanceFilter === item.value}
                  onChange={() => setBalanceFilter(item.value)}
                  className="w-3 h-3 text-[#303F9F] accent-[#303F9F] cursor-pointer"
                />
                <span className="text-[11px] font-bold text-slate-700">
                  {item.label}
                </span>
              </label>
            ))}
          </div>

          {/* Report Type Row */}
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-col gap-0.5">
              <label className="text-[11px] font-bold text-slate-800">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-medium text-slate-700 outline-none bg-white cursor-pointer"
              >
                <option value="Summary" className="bg-white text-slate-800">
                  Summary
                </option>
                <option value="Simple" className="bg-white text-slate-800">
                  Simple
                </option>
                <option
                  value="Ledger Model"
                  className="bg-white text-slate-800"
                >
                  Ledger Model
                </option>
                <option value="Group Wise" className="bg-white text-slate-800">
                  Group Wise
                </option>
                <option
                  value="Group & Ledger"
                  className="bg-white text-slate-800"
                >
                  Group & Ledger
                </option>
                <option
                  value="PV/RV Report"
                  className="bg-white text-slate-800"
                >
                  PV/RV Report
                </option>
                <option
                  value="Group Wise PV/RV Report"
                  className="bg-white text-slate-800"
                >
                  Group Wise PV/RV Report
                </option>
                <option
                  value="Balance order by Date"
                  className="bg-white text-slate-800"
                >
                  Balance order by Date
                </option>
                <option
                  value="Sales Return"
                  className="bg-white text-slate-800"
                >
                  Sales Return
                </option>
                <option value="Monthly" className="bg-white text-slate-800">
                  Monthly
                </option>
                <option value="Custom 1" className="bg-white text-slate-800">
                  Custom 1
                </option>
              </select>
            </div>
          </div>

          {/* Tabs Container */}
          <div className="border border-slate-300 rounded bg-[#F5F7FB] flex flex-col gap-1.5 overflow-hidden">
            <div className="flex border-b border-[#b8cce0] gap-0.5 px-2  bg-slate-100">
              {["Area", "Delivery Day", "SalesMan"].map((tab) => {
                const isSelected = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-[25px] py-[6px] text-[13px] font-medium cursor-pointer transition-all duration-150 font-sans border-t-0 border-x-0 border-b-[3px] border-solid ${
                      isSelected
                        ? "bg-[#E8EEF8] text-[#2C4ABB] rounded-t-xl border-[#2C4ABB] -mb-[1px]"
                        : "bg-transparent text-[#1a2e4a] hover:bg-[#1a2e4a14] border-transparent"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            <div className="p-2 pt-0.5">
              {activeTab === "Area" && (
                <div className="grid grid-cols-2 gap-2">
                  {/* Left Column: Route */}
                  <div className="flex flex-col gap-1">
                    <div className="border border-slate-300 rounded overflow-hidden flex flex-col bg-white h-[145px]">
                      <div className="bg-[#1A237E] text-white px-2.5 py-1.5 border-b border-slate-300 text-[10px] font-bold uppercase tracking-wider">
                        Route
                      </div>

                      {/* Scrollable list */}
                      <div className="p-0.5 flex flex-col gap-0.5 flex-1 overflow-y-auto bg-white [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-transparent">
                        {filteredRoutes.map((route) => (
                          <label
                            key={route.id}
                            className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer transition-all ${
                              route.checked
                                ? "bg-[#D2D7F3] text-[#1A237E] font-bold"
                                : "hover:bg-slate-100 text-slate-800"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={route.checked}
                              onChange={() => handleRouteCheck(route.id)}
                              className="w-3 h-3 rounded border-slate-300 accent-[#303F9F] cursor-pointer"
                            />
                            <span className="text-[10px] uppercase">
                              {route.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-1 justify-center">
                      <button
                        type="button"
                        onClick={selectAllRoutes}
                        className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={unselectAllRoutes}
                        className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                      >
                        UnSelect All
                      </button>
                    </div>
                  </div>

                  {/* Right Column: Area */}
                  <div className="flex flex-col gap-1">
                    <div className="border border-slate-300 rounded overflow-hidden flex flex-col bg-white h-[145px]">
                      <div className="bg-[#1A237E] text-white px-2.5 py-1.5 border-b border-slate-300 text-[10px] font-bold uppercase tracking-wider">
                        Area
                      </div>

                      {/* Scrollable list */}
                      <div className="p-0.5 flex flex-col gap-0.5 flex-1 overflow-y-auto bg-white [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-transparent">
                        {filteredAreas.map((area) => (
                          <label
                            key={area.id}
                            className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer transition-all ${
                              area.checked
                                ? "bg-[#D2D7F3] text-[#1A237E] font-bold"
                                : "hover:bg-slate-100 text-slate-800"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={area.checked}
                              onChange={() => handleAreaCheck(area.id)}
                              className="w-3 h-3 rounded border-slate-300 accent-[#303F9F] cursor-pointer"
                            />
                            <span className="text-[10px] uppercase">
                              {area.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-1 justify-center">
                      <button
                        type="button"
                        onClick={selectAllAreas}
                        className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={unselectAllAreas}
                        className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                      >
                        UnSelect All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Delivery Day" && (
                <div className="flex flex-col gap-1 w-full">
                  <div className="border border-slate-300 rounded overflow-hidden flex flex-col bg-white h-[145px]">
                    <div className="bg-[#1A237E] text-white px-2.5 py-1.5 border-b border-slate-300 text-[10px] font-bold uppercase tracking-wider">
                      Name
                    </div>
                    {/* Scrollable list */}
                    <div className="p-0.5 flex flex-col gap-0.5 flex-1 overflow-y-auto bg-white [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-transparent">
                      {deliveryDays.map((day) => (
                        <label
                          key={day.id}
                          className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded cursor-pointer transition-all border-b border-slate-100 last:border-b-0 ${
                            day.checked
                              ? "bg-[#D2D7F3] text-[#1A237E] font-bold"
                              : "hover:bg-slate-100 text-slate-800"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={day.checked}
                            onChange={() => handleDeliveryDayCheck(day.id)}
                            className="w-3 h-3 rounded border-slate-300 accent-[#303F9F] cursor-pointer"
                          />
                          <span className="text-[10px] uppercase font-semibold">
                            {day.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1 justify-center mt-0.5">
                    <button
                      type="button"
                      onClick={selectAllDeliveryDays}
                      className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={unselectAllDeliveryDays}
                      className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      UnSelect All
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "SalesMan" && (
                <div className="flex flex-col gap-1 w-full">
                  <div className="border border-slate-300 rounded overflow-hidden flex flex-col bg-white h-[145px]">
                    <div className="bg-[#1A237E] text-white px-2.5 py-1.5 border-b border-slate-300 text-[10px] font-bold uppercase tracking-wider">
                      Name
                    </div>
                    {/* Scrollable list */}
                    <div className="p-0.5 flex flex-col gap-0.5 flex-1 overflow-y-auto bg-white [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-transparent">
                      {salesmen.map((sm) => (
                        <label
                          key={sm.id}
                          className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded cursor-pointer transition-all border-b border-slate-100 last:border-b-0 ${
                            sm.checked
                              ? "bg-[#D2D7F3] text-[#1A237E] font-bold"
                              : "hover:bg-slate-100 text-slate-800"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={sm.checked}
                            onChange={() => handleSalesmanCheck(sm.id)}
                            className="w-3 h-3 rounded border-slate-300 accent-[#303F9F] cursor-pointer"
                          />
                          <span className="text-[10px] uppercase font-semibold">
                            {sm.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1 justify-center mt-0.5">
                    <button
                      type="button"
                      onClick={selectAllSalesmen}
                      className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={unselectAllSalesmen}
                      className="px-1.5 py-0.5 border border-slate-300 bg-white text-slate-700 text-[9px] font-bold rounded shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      UnSelect All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between mt-1 pt-1.5 border-t border-slate-100 shrink-0">
            {/* Exclude SubGroup Checkbox */}
            <label className="flex items-center gap-1.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={excludeSubGroup}
                onChange={(e) => setExcludeSubGroup(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 accent-[#303F9F] cursor-pointer"
              />
              <span className="text-[11px] font-bold text-slate-700 group-hover:text-slate-900 transition-colors">
                Exclude SubGroup
              </span>
            </label>

            {/* Action Buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  if (fromDate && !isDateInFy(fromDate)) {
                    alert("From date incompatible with financial year");
                    return;
                  }
                  if (toDate && !isDateInFy(toDate)) {
                    alert("To date incompatible with financial year");
                    return;
                  }
                  if (openReport) {
                    const payload = {
                      groupName: groupName?.value || "",
                      fromDate,
                      toDate,
                      reportType,
                      balanceFilter,
                      excludeSubGroup,
                      routes: routes.filter((r) => r.checked).map((r) => r.id),
                      areas: areas.filter((a) => a.checked).map((a) => a.id),
                      salesmen: salesmen
                        .filter((s) => s.checked)
                        .map((s) => s.id),
                      deliveryDays: deliveryDays
                        .filter((d) => d.checked)
                        .map((d) => d.id),
                      fyid,
                    };
                    openReport(payload);
                  }
                }}
                className="flex flex-col items-center justify-center w-[56px] h-[46px] rounded border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2E7D32"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mb-0.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="text-[10px] font-bold text-[#303F9F]">
                  Show
                </span>
              </button>
              <button
                onClick={onClose}
                className="flex flex-col items-center justify-center w-[56px] h-[46px] rounded border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#D32F2F"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mb-0.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span className="text-[10px] font-bold text-slate-800">
                  Exit
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupList;
